import React, { useState } from 'react'
import { useGlobalFilter, useTable } from 'react-table'
import { Box, CircularProgress } from '@mui/material'
import { t } from 'i18next'
import './DynamicTable.css'

function DynamicTable({
  columns,
  data,
  loadingData,
  enableRowDrag = false,
  getRowId,
  onRowsReorder,
}) {
  const [overIndex, setOverIndex] = useState(null)

  const tableInstance = useTable(
    {
      columns,
      data: data ?? [],
      getRowId: getRowId || ((row, index) => row.id || String(index)),
    },
    useGlobalFilter
  )

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance

  const reorderRows = (fromIndex, toIndex) => {
    if (fromIndex === null || toIndex === null || fromIndex === toIndex) return

    const reorderedData = [...(data ?? [])]
    const [movedItem] = reorderedData.splice(fromIndex, 1)
    reorderedData.splice(toIndex, 0, movedItem)

    onRowsReorder?.(reorderedData)
  }

  const handleDragStart = (event, rowIndex) => {
    if (!enableRowDrag) return

    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', String(rowIndex))
  }

  const handleDragOver = (event, rowIndex) => {
    if (!enableRowDrag) return

    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    setOverIndex(rowIndex)
  }

  const handleDrop = event => {
    if (!enableRowDrag) return

    event.preventDefault()

    const fromIndex = Number(event.dataTransfer.getData('text/plain'))

    reorderRows(fromIndex, overIndex)

    setOverIndex(null)
  }

  const handleDragEnd = () => {
    setOverIndex(null)
  }

  return (
    <div
      className="table-container-search-invoice"
      data-testid="container-dynamic-table">
      <div
        className="table-wrapper-search-invoice"
        data-testid="container-table-wrapper-search-invoice">
        <table
          {...getTableProps()}
          className="dynamic-table-no-horizontal-borders"
          data-testid="table-dynamic">
          <thead>
            {headerGroups.map((headerGroup, groupIndex) => (
              <tr
                {...headerGroup.getHeaderGroupProps()}
                key={`header-group-${groupIndex}`}>
                {headerGroup.headers.map((column, columnIndex) => (
                  <th
                    {...column.getHeaderProps()}
                    style={{ width: column.width || 'auto' }}
                    key={`header-${groupIndex}-${column.id}-${columnIndex}`}>
                    {column.render('Header')}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {loadingData && (
              <tr key="loading-row">
                <td colSpan={columns.length}>
                  <Box className="loader-search-invoice">
                    <CircularProgress color="primary" size={30} />
                  </Box>
                </td>
              </tr>
            )}
            {!loadingData && data?.length === 0 && (
              <tr key="no-data-row">
                <td colSpan={columns.length}>
                  <Box className="not-search-invoice-data">
                    {t('GENERIC_MODAL.NO_ITEMS_FOUND')}
                  </Box>
                </td>
              </tr>
            )}
            {rows?.map((row, rowIndex) => {
              prepareRow(row)
              return (
                <tr
                  {...row.getRowProps()}
                  key={`row-${rowIndex}-${row.id || rowIndex}`}
                  onDragOver={event => handleDragOver(event, rowIndex)}
                  onDrop={handleDrop}
                  className={
                    enableRowDrag && overIndex === rowIndex
                      ? 'dynamic-table-row-drag-over'
                      : ''
                  }>
                  {row.cells.map((cell, cellIndex) => (
                    <td
                      style={{ width: cell.column.width || 'auto' }}
                      {...cell.getCellProps()}
                      key={`cell-${rowIndex}-${cellIndex}-${cell.column.id}`}>
                      {cell.column.id === 'drag' && enableRowDrag ? (
                        <span
                          draggable
                          onDragStart={event =>
                            handleDragStart(event, rowIndex)
                          }
                          onDragEnd={handleDragEnd}
                          className="dynamic-table-drag-handle"
                          title={t(
                            'CUSTOMER_DISPLAY_SETTINGS.LIST.DRAG_TO_REORDER'
                          )}>
                          {cell.render('Cell')}
                        </span>
                      ) : (
                        cell.render('Cell')
                      )}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default DynamicTable
